import { useEffect, useState } from 'react';
import {
  KpiDataVersionChangeAudit,
  KpiCompany,
  KpiDataVersion,
  KpiDataVersionData,
  KpiIndicator,
  KpiTemplate,
  KpiTemplateIndicator,
  useKpiCompanyGetQuery,
  useKpiCompanyListQuery,
  useKpiDataVersionGetCurrentQuery,
  useKpiCompanySetDataCollectionFrequencyMutation,
  useKpiCompanySetDataCollectionStartDateMutation,
  useKpiDataVersionUpdateDataMutation,
  useKpiDataVersionListAuditsQuery,
  useKpiTemplateGetQuery,
  useKpiDataVersionPublishMutation,
  useKpiDataVersionGetPublishedQuery,
} from 'generated/graphql';

import moment, { Moment } from 'moment';
// @ts-ignore
import { orderBy, uniqBy } from 'lodash';
import { toJS } from 'mobx';
import { useStores } from 'hooks/useStores';
import { useLibrary } from './useLibrary';
import {
  useKpiDataVersionUnlockMutation,
  useKpiDataVersionLockMutation,
} from 'generated/graphql';
import { IBaseResponse } from 'types/common/baseResponse';

export const useDataEntry = (publishedOnly: boolean = false) => {
  const { loading: companyLoading, data: companyData } = useKpiCompanyGetQuery({
    skip: true,
  });

  const { KPIStore } = useStores();
  const { operations } = useLibrary();
  const [company, setCompany] = useState<KpiCompany>();
  const [template, setTemplate] = useState<KpiTemplate>({});
  const [settings, setSettings] = useState({
    period: 'M',
    startDate: null,
  });
  const [columns, setColumns] = useState<any>();
  const [localChanges, setLocalChanges] = useState<any[]>([]);
  const [dataVersion, setDataVersion] = useState<any>();
  const [audits, setAudits] = useState<KpiDataVersionChangeAudit[]>([]);
  const [rows, setRows] = useState<any>();
  const [kpiGroups, setKpiGroups] = useState<any>();
  const [activities, setActivities] = useState<any[]>([]);
  const { refetch: currentDataVersionRefetch } = useKpiDataVersionGetCurrentQuery({
    skip: true,
  });
  const { refetch: publishedDataVersionRefetch } = useKpiDataVersionGetPublishedQuery({
    skip: true,
  });
  const [isLoading, setLoading] = useState(true);
  const { refetch: getTemplate } = useKpiTemplateGetQuery({
    skip: true,
  });
  const { refetch: getAudits } = useKpiDataVersionListAuditsQuery({
    skip: true,
  });
  const [
    setUnlock,
    { loading: unlockLoading },
  ] = useKpiDataVersionUnlockMutation();
  const [setLock, { loading: lockLoading }] = useKpiDataVersionLockMutation();
  const [
    updateDataVersion,
    { loading: updateLoading },
  ] = useKpiDataVersionUpdateDataMutation();
  const [
    publishDataVersion,
    { loading: publishLoading },
  ] = useKpiDataVersionPublishMutation();

  const [
    setPeriodSetting,
    { error: periodError },
  ] = useKpiCompanySetDataCollectionFrequencyMutation();
  const [
    setStartDateSetting,
  ] = useKpiCompanySetDataCollectionStartDateMutation();

  const [companies, setCompanies] = useState<any[]|null>(null);
  // todo(vmyshko): add isEditing to disable company dd AC3

  const {
    loading: getCompaniesLoading,
    refetch: getCompanies,
  } = useKpiCompanyListQuery({ skip: true });

  useEffect(() => {
    if (company) {
      getAudits({
        companyId: company.id || undefined,
      }).then((e) => {
        setAudits(
          (e.data.kpiDataVersionListAudits ||
            []) as KpiDataVersionChangeAudit[],
        );
      });
    }
  }, [company]);

  useEffect(() => {
    if (companyData) {
      // @ts-ignore
      setCompany(companyData.kpiCompanyGet);
      getKpiGroupsFromTemplate(company?.templateId || '').then();
    }
  }, [companyData, companyLoading]);

  useEffect(() => {
    if (periodError) {
      console.error('Failed to set period', periodError);
    }
  }, [periodError]);

  useEffect(() => {
    if (dataVersion) {
      getKpiGroupsFromTemplate(company?.templateId || '').then();
      setActivities(dataVersion.changeAudit || []);
    }
  }, [dataVersion]);

  // todo(vmyshko): seems its broken
  useEffect(() => {
    if (settings.startDate && company) {
      if (!company.templateId) {
        console.warn('Failed to find template for company: ' + company.name);
        //TODO - convert to base response, with error template was not found
      } else {
        if (publishedOnly) {
          publishedDataVersionRefetch({ companyId: company.id || undefined }).then(
            async (response) => {
              if (!response.data?.kpiDataVersionGetPublished) {
                //TODO - display error message
              } else {
                setDataVersion(response?.data?.kpiDataVersionGetPublished);
              }
            },
          );
        } else {
          currentDataVersionRefetch({ companyId: company.id || undefined }).then(
            async (response) => {
              if (!response.data?.kpiDataVersionGetCurrent) {
                //TODO - display error message
              } else {
                setDataVersion(response?.data?.kpiDataVersionGetCurrent);
              }
            },
          );
        }
      }
    }
  }, [settings.startDate, company]);

  useEffect(() => {
    if (company && company.templateId)
      (async () => {
        await getKpiGroupsFromTemplate(company.templateId || '');
      })();
  }, [settings.period, settings.startDate]);

  useEffect(() => {
    setLoading(
      companyLoading ||
        unlockLoading ||
        lockLoading ||
        updateLoading ||
        publishLoading,
    );
  }, [
    companyLoading,
    unlockLoading,
    lockLoading,
    updateLoading,
    publishLoading,
  ]);

  const loadSettings = async (companyData: KpiCompany) => {
    if (companyData) {
      const frequencies = companyData.dataCollectionFrequency;
      const startDates = companyData.dataCollectionStartDate;
      let period = settings.period;
      let startDate = settings.startDate;
      //TODO - remove this part when default values will came from server side.
      if (frequencies?.length === 0) {
        await setPeriodSetting({
          variables: { id: companyData.id || '', period: 'Q' },
        });
      } else {
        // @ts-ignore
        period = frequencies[frequencies?.length - 1]?.period.toUpperCase();
        setSettings({
          startDate,
          period,
        });
      }

      if (startDates?.length === 0) {
        await setStartDateSetting({
          variables: {
            id: companyData.id || '',
            date: moment().subtract(2, 'years').toString(),
          },
        });
      } else {
        setSettings({
          period,
          // @ts-ignore
          startDate: startDates[startDates?.length - 1].date,
        });
      }
    } else {
      return [];
    }
  };

  const saveSettings = async (companyData: KpiCompany, settingsData: any) => {
    let periodResponseResult: IBaseResponse<KpiCompany> = {
      errorCode: 0,
    };
    let startDateResponseResult: IBaseResponse<KpiCompany> = {
      errorCode: 0,
    };
    if (settings.period !== settingsData.period) {
      const periodResponse = await setPeriodSetting({
        variables: { id: companyData.id || '', period: settingsData.period },
      });
      periodResponseResult = {
        payload:
          periodResponse.data?.kpiCompanySetDataCollectionFrequency ||
          undefined,
        errorCode: 0,
      };
      if (periodResponse?.errors) {
        return {
          ...periodResponseResult,
          errorCode: periodResponse?.errors?.length === 0 ? 0 : -1,
          errorMessage: periodResponse?.errors[0].message,
        };
      }
    }
    if (settings.startDate !== settingsData.startDate) {
      const startDateResponse = await setStartDateSetting({
        variables: {
          id: companyData.id || '',
          date: settingsData.startDate,
        },
      });
      startDateResponseResult = {
        payload:
          startDateResponse.data?.kpiCompanySetDataCollectionStartDate ||
          undefined,
        errorCode: 0,
      };
      if (startDateResponse?.errors) {
        return {
          ...startDateResponseResult,
          errorCode: startDateResponse?.errors.length === 0 ? 0 : -1,
          errorMessage: startDateResponse?.errors[0].message,
        };
      }
    }
    if (
      periodResponseResult.errorCode === 0 &&
      startDateResponseResult.errorCode === 0
    ) {
      await updateData();
      setSettings(settingsData);
    }
    const resultResponse: IBaseResponse<KpiCompany> = { errorCode: 0 };
    return resultResponse;
  };

  const sortCompanies = (companies: KpiCompany[]) => {
    return orderBy(
      companies,
      [
        (o: any) => Boolean(o.templateId),
        (o: any) => new Date(o.updated).getTime(),
        (o: any) => o.name,
      ],
      ['desc', 'desc', 'asc'],
    );
  };

  const loadCompanies = async () => {
    const companiesResponse = await getCompanies();
    const companies = companiesResponse.data?.kpiCompanyList;
    // @ts-ignore
    const sortedCompanies = sortCompanies(companies);
    setCompanies(sortedCompanies);
    return sortedCompanies;
  };

  const loadCompanyById = async (id: string) => {
    const companies = await loadCompanies();
    const company = companies?.find((c: any) => c?.id === id) as KpiCompany;
    setCompany(company);
    return company;
  };

  const loadCompanyByExtId = async (extId: string) => {
    const companies = await loadCompanies();
    const company = companies?.find((c: any) => c?.extId === extId) as KpiCompany;
    setCompany(company);
    return company;
  };

  const loadDefaultCompany = async () => {
    const companies = await loadCompanies();
    if (companies.length) return companies[0];
    return null;
  };

  const parsePeriod = (period: string) => {
    const periodTimes = period.split('-');
    const year = `20${periodTimes[1]}`;
    const type = period[0];
    const num = parseInt(periodTimes[0].replace(type, ''));

    if (type === 'Q') {
      // Q3-19 // M01-19
      return moment(year).quarter(num);
    } else {
      return moment(year).month(num - 1);
    }
  };

  const formatDate = (period: string, date: any) => {
    const mFmt = '[M]MM-YY';
    const qFmt = '[Q]Q-YY';
    if (period === 'Q') {
      return date.format(qFmt);
    } else {
      return date.format(mFmt);
    }
  };

  const buildMatrix = (groupedKPIs: any, dataVersion: KpiDataVersionData[]) => {
    const { startDate, period } = settings;
    const matrix: any = {};
    let columnsToAdd: any = [];
    groupedKPIs.forEach((group: any) => {
      matrix[group.name] = [];

      group.kpis.forEach((kpi: any) => {
        let item = {} as any;

        item.name = kpi.name;
        item.type = kpi.type;
        item.id = kpi.id;
        dataVersion
          .filter((d) => d.indicatorId === kpi.id)
          .forEach((r) => {
            // Filing row data
            // @ts-ignore
            item[r.period] = r.value;

            const existingPeriod = columnsToAdd.find(
              (c: any) => c.accessor === r.period,
            );
            if (!existingPeriod) {
              columnsToAdd.push(
                getGridColumn(parsePeriod(r.period || ''), r.period || ''),
              );
            }
          });
        matrix[group.name].push(item);
      });

      matrix[group.name] = matrix[group.name].sort(
        (a: any, b: any) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
    });

    columnsToAdd.sort(function (left: any, right: any) {
      return moment.utc(left.date).diff(moment.utc(right.date));
    });

    let minDate = moment(startDate).startOf('M');
    if (columnsToAdd.length !== 0) {
      if (columnsToAdd[0].date <= minDate)
        minDate = moment(columnsToAdd[0].date);
    }

    // @ts-ignore
    const currentDate = moment().startOf(period);

    while (minDate <= currentDate) {
      const currAccessor = formatDate(period, minDate);
      const exitingColumn = columnsToAdd.find(
        (c: any) => c.accessor === currAccessor,
      );

      // If column doesnt exists we need to create it
      if (!exitingColumn) {
        const startQuarter = moment(minDate).startOf('Q');
        const quaterAccessor = formatDate('Q', startQuarter);

        if (period === 'Q') {
          const endQuarter = moment(minDate).endOf('Q');
          const foundMonthly = columnsToAdd.find((c: any) =>
            c.date.isBetween(startQuarter, endQuarter),
          );
          if (!foundMonthly) {
            columnsToAdd.push(getGridColumn(minDate, currAccessor));
          } else {
            // this Q has monthly data - convert to monthly
            let date = startQuarter;
            while (date < endQuarter) {
              const monthlyAccessor = formatDate('M', date);
              if (
                !columnsToAdd.find((c: any) => c.accessor === monthlyAccessor)
              ) {
                columnsToAdd.push(getGridColumn(date, monthlyAccessor));
              }

              date.add('M', 1);
            }
          }
        } else {
          const foundQuater = columnsToAdd.find(
            (c: any) => c.accessor === quaterAccessor,
          );
          if (!foundQuater) {
            columnsToAdd.push(getGridColumn(minDate, currAccessor));
          }
        }
      }
      // @ts-ignore
      minDate.add(1, period);
      // @ts-ignore
      minDate = minDate.startOf(period);
    }

    columnsToAdd.sort(function (left: any, right: any) {
      return moment.utc(left.date).diff(moment.utc(right.date));
    });

    return { rows: matrix, columns: columnsToAdd.map((c: any) => c.id) };
  };

  const getGridColumn = (date: Moment, period: string) => {
    return {
      id: period,
      Header: period,
      period: period,
      accessor: period,
      date: moment(date),
    };
  };

  const getKpiGroupsFromTemplate = async (templateId: string) => {
    const template = await getTemplate({ id: templateId });
    // @ts-ignore
    setTemplate(template?.data?.kpiTemplateGet);
    let templatesKpis: KpiIndicator[] = [];

    template.data?.kpiTemplateGet?.indicators?.map(
      // @ts-ignore
      (kpiIndicator: KpiTemplateIndicator) => {
        // @ts-ignore
        const kpi = toJS(KPIStore.kpisByIds[kpiIndicator.id]);
        if (!kpi) {
          console.error(
            'Falied to find KPI from mobx store: ' + kpiIndicator.id,
          );
          return;
        }
        templatesKpis.push(kpi);
      },
    );

    const { groupedKPIs } = operations.loadGroupedKPIs(templatesKpis);

    setKpiGroups(groupedKPIs);
    const { rows, columns: newColumns } = buildMatrix(
      groupedKPIs,
      dataVersion?.data ?? [],
    );
    setColumns(newColumns);
    setRows(rows);

    return groupedKPIs;
  };

  const updateData = async () => {
    // map old rows to KpiDataVersion?
    const oldRowChanges = Object.values(rows)
      .flatMap((x) => x)
      .flatMap((row) => {
        const { name, type, id, ...rest } = row as any;
        return Object.entries(rest).map(([key, value]) => ({
          indicatorId: id,
          name,
          period: key,
          type,
          value,
        }));
      });

    const uniqueLocalChanges = uniqBy(
      localChanges.reverse(),
      (lc: any) => lc.indicatorId + lc.period,
    );

    const mixedChanges = oldRowChanges.filter((orc) => {
      return !uniqueLocalChanges.some(
        (lc: any) =>
          lc.indicatorId === orc.indicatorId && lc.period === orc.period,
      );
    });
    let response = null;
    try {
      const dataToUpdate = [...mixedChanges, ...uniqueLocalChanges].filter(
        (c) => c.value !== '',
      );
      response = await updateDataVersion({
        variables: {
          companyId: company?.id || '',
          data: dataToUpdate,
        },
      });
    } catch (err) {
      const updateResponse: IBaseResponse<KpiDataVersionData> = {
        errorCode: -1,
        payload: undefined,
        // @ts-ignore
        errorMessage: response?.error || response,
      };
      return updateResponse;
    }

    const updateResponse: IBaseResponse<KpiDataVersionData> = {
      errorCode: 0,
      // @ts-ignore
      payload: response.data,
      // @ts-ignore
      errorMessage: response.error,
    };

    setDataVersion({
      ...dataVersion,
      data: response?.data?.kpiDataVersionUpdateData,
    });

    // Reset local changes
    setLocalChanges([]);
    return updateResponse;
  };

  return {
    models: {
      audits,
      columns,
      rows,
      company,
      template,
      companies,
      companiesLoading: getCompaniesLoading,
      isLoading,
      kpiGroups,
      dataVersion,
      settings,
      activities,
      hasUnsavedChanges: localChanges.length > 0,
    },
    operations: {
      loadCompany: async (id: string) => {
        const company = await loadCompanyById(id);
        loadSettings(company).then();
        return company;
      },
      loadCompanyByExtId: async (extId: string) => {
        const company = await loadCompanyByExtId(extId);
        loadSettings(company).then();
        return company;
      },
      loadDefaultCompany: async () => {
        return await loadDefaultCompany();
      },
      lock: async () => {
        try {
          const lockResponse = await setLock({
            variables: { companyId: company?.id || '' },
          });
          setDataVersion(lockResponse.data?.kpiDataVersionLock);

          const lock: IBaseResponse<KpiDataVersion> = {
            errorCode: lockResponse?.data ? 0 : -1,
            // @ts-ignore
            payload: lockResponse?.data?.kpiDataVersionLock,
            errorMessage:
              (lockResponse?.errors?.length || 0) > 0
                ?
                  // @ts-ignore
                  lockResponse?.errors[0].message?.message
                : null,
          };
          return lock;
        } catch (err: any) {
          const lock: IBaseResponse<KpiDataVersion> = {
            errorCode: -1,
            payload: err.data?.kpiDataVersionLock,
            errorMessage: err.graphQLErrors?.[0].message.message ?? err,
          };

          return lock;
        }
      },
      unLock: async (companyId?: string) => {
        try {
          const unLockResponse = await setUnlock({
            variables: { companyId: company?.id || companyId || '' },
          });
          const unLock: IBaseResponse<KpiDataVersion> = {
            errorCode: unLockResponse?.data ? 0 : -1,
            // @ts-ignore
            payload: unLockResponse.data?.kpiDataVersionUnlock,
            errorMessage:
              (unLockResponse?.errors?.length || 0) > 0
                ?
                  // @ts-ignore
                  unLockResponse?.errors[0]?.message?.message
                : null,
          };
          return unLock;
        } catch (err: any) {
          const unLock: IBaseResponse<KpiDataVersion> = {
            errorCode: -1,
            payload: err.data?.kpiDataVersionLock,
            errorMessage: err.graphQLErrors[0]?.message.message,
          };

          return unLock;
        }
      },
      saveSettings,
      saveLocalChanges: (
        groupName: string,
        changedDataItem: KpiDataVersionData,
      ) => {
        setLocalChanges([...localChanges, changedDataItem]);
      },
      cancelLocalChanges: () => {
        setLocalChanges([]);
      },
      publishData: async () => {
        const publishResponse = await publishDataVersion({
          variables: {
            companyId: company?.id || '',
          },
        });
        const updateResponseResult: IBaseResponse<KpiDataVersion> = {
          errorCode: 0,
          // @ts-ignore
          payload: publishResponse.data,
          // @ts-ignore
          errorMessage: publishResponse.error,
        };
        // @ts-ignore
        return updateResponseResult;
      },
      updateData,
    },
  };
};
